import { useCallback, useEffect, useRef, useState } from 'react';
import { getMessageImage, isError } from '../api';

/**
 * The page of their own book a question is about (F-46, shown at last by F-150).
 *
 * **`ItemMedia` shipped in F-46 and nothing has ever displayed one.** Generation asks the
 * model for a picture, refuses an item whose picture it cannot pin to a key *and* an S3
 * version, and writes `alt` because a question sent without the image had to stay
 * answerable — and then the only interface the learner has had since F-103 contained no
 * `<img>` at all. So the pipeline could write *"which point is arrowed here"* about a
 * photograph the chat could not show. This is the display half.
 *
 * ## A missing image is the ordinary case, not an error
 *
 * Four of them, and none is a fault worth a face: the presigned URL expires, the learner is
 * on a train, the object was deleted with the source it came from, or the row predates the
 * media being logged. **`alt` exists for exactly this**, so it renders as content — the
 * sentence the model wrote about the picture, in the bubble, where a learner who cannot see
 * the photograph can still answer the question. Nothing says "failed to load": what failed
 * is ours and the learner can do nothing about it, and a broken-image glyph in the middle of
 * somebody's study material reads as the product being broken rather than as a slow
 * connection.
 *
 * The description renders *beside* the image as well as in its place. It is the caption a
 * photograph of a page needs anyway — "Figure 4.2, the lung channel, with LU 7 arrowed" —
 * and keeping it visible is what makes the fallback not a change of layout.
 *
 * ## The URL is signed when the picture is about to be seen, not when the bubble mounts
 *
 * **This is a correctness rule and not an optimisation, which the first version of this file
 * got wrong.** A presign lives five minutes (`PRESIGN_TTL_SECONDS`), a page of the thread is
 * fifty messages, and a bubble is keyed on its message id so it mounts once and never again.
 * Signing at mount therefore meant: open the chat, fifty bubbles mount, every image message
 * signs a URL, the learner reads for six minutes, scrolls up to the image question — and S3
 * answers 403. The caption fallback made that *indistinguishable from the photograph being
 * missing*, which is this project's signature failure wearing the feature's own graceful
 * degradation as a disguise.
 *
 * So the presign waits for an `IntersectionObserver`, and a fetch that fails is retried once
 * with a freshly signed URL before the picture is given up on. The retry is what covers the
 * other expiries nothing here can predict: a learner who leaves the tab open on an image for
 * six minutes and reloads it, and the Lambda's own session token, which can expire sooner
 * than the five minutes the signature asks for.
 *
 * `loading="lazy"` is deliberately *not* set. The observer already decides when the bytes are
 * wanted, and the attribute deferring the fetch a second time is what turned a signed URL
 * into an expired one.
 *
 * ## Its own request, and not a field on the thread
 *
 * `GET /api/chat` is polled every twenty seconds for the life of an open tab. A URL that
 * expires in five minutes would be re-signed on every one of those polls, for every image on
 * the page, whether or not anybody ever looked at one.
 */
export default function Picture({ messageId, alt }: { messageId: string; alt: string }) {
  const [url, setUrl] = useState<string>();
  /*
   * Set when the bytes will not arrive, and it is the *only* thing that hides the `<img>`.
   * A separate flag from `url` being absent, because "not asked for yet" and "will not
   * arrive" want the same rendering and different transitions: without it a failed image
   * keeps its element, the browser keeps its broken-image glyph, and the description is
   * announced twice to a screen reader.
   */
  const [gone, setGone] = useState(false);
  /** One retry, so an expired signature is not the end of it and a dead object is. */
  const retried = useRef(false);
  const figure = useRef<HTMLElement>(null);

  const mint = useCallback(() => {
    let live = true;
    getMessageImage(messageId)
      .then((answer) => {
        if (!live) return;
        if (isError(answer)) setGone(true);
        else setUrl(answer.url);
      })
      // Never rethrown, and the learner is never told: a presign that could not be fetched
      // is the same outcome as one that expired, and they have the description either way.
      // Logged, though — "this objective asks image questions and never shows a picture" is
      // otherwise indistinguishable from "this objective has no pictures".
      .catch((error: unknown) => {
        console.warn('chat image unavailable', messageId, error);
        if (live) setGone(true);
      });
    return () => {
      live = false;
    };
  }, [messageId]);

  useEffect(() => {
    retried.current = false;
    setUrl(undefined);
    setGone(false);

    const element = figure.current;
    /*
     * No observer, no deferral: an old browser gets the picture eagerly rather than never.
     * Degrading to the behaviour this replaced is the right failure here — it costs a fetch
     * nobody asked for and shows the learner their page.
     */
    if (element === null || typeof IntersectionObserver === 'undefined') return mint();

    let cancel: (() => void) | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        // Once only. The signature is wanted when the picture is first approached, not
        // every time it crosses the edge of the screen on the way past.
        observer.disconnect();
        cancel = mint();
      },
      // A screen ahead, so the bytes are usually there by the time the bubble is.
      { rootMargin: '400px' },
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
      cancel?.();
    };
  }, [messageId, mint]);

  const failed = () => {
    if (retried.current) {
      setGone(true);
      return;
    }
    // Almost always an expired signature, which a new one fixes. A deleted object fails the
    // same way and stops here, which is why it is one retry and not a loop.
    retried.current = true;
    setUrl(undefined);
    mint();
  };

  return (
    <figure className="chat-msg-figure" ref={figure}>
      {url !== undefined && !gone && (
        <img
          className="chat-msg-image"
          src={url}
          // Empty, because the description is already on the page as the caption below and a
          // screen reader that read both would announce the same sentence twice. The image
          // is not decoration — the caption is its text, which is what `<figcaption>` is.
          alt=""
          onError={failed}
        />
      )}
      <figcaption className="chat-msg-caption">{alt}</figcaption>
    </figure>
  );
}
